/* eslint-disable max-len */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
    Property,
    SourcingSettings,
    Supplier,
} from 'entities/Property/model/types/PropertySchema';
import { navigationBlockModalActions } from 'features/NavigationBlockModal/model/slices/NavigationBlockModalSlice';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';

import {
    getIsLoadingSourcingModes,
    getIsLoadingSuppliers,
    getIsSaveSettings,
    getListOfSuppliers,
} from '../model/selectors/ProductSourcingSelectors';
import {
    ProductPriceSortingModes,
    ProductSourcingModes,
} from '../model/types/ProductSourcingSchema';

type ProductSourcingSettings = {
    mode: string;
    preferredSuppliersIds?: string[];
    propertyId: string;
    orderType: string;
    sortingMode: string;
};

export const useProductSourcingSettings = (
    selectedProperty: Property,
    orderType: string,
    onApplySettingsToProperty: (
        settings: ProductSourcingSettings,
    ) => Promise<void>,
) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const isLoadingSuppliers = useSelector(getIsLoadingSuppliers);
    const listOfSuppliers = useSelector(getListOfSuppliers);
    const isSaveSettings = useSelector(getIsSaveSettings);
    const isLoadingSourcingModes = useSelector(getIsLoadingSourcingModes);
    const isLoadingData = isLoadingSuppliers || isLoadingSourcingModes;

    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [preferredSuppliers, setPreferredSuppliers] = useState<Supplier[]>(
        [],
    );
    const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>(
        [],
    );
    const [enablePreferredSuppliers, setEnablePreferredSuppliers] =
        useState(false);
    const [preferredSuppliersOnly, setPreferredSuppliersOnly] = useState(false);
    const [sortingMode, setSortingMode] = useState<string>(
        ProductPriceSortingModes.BestPrice,
    );

    const sourcingSettings: SourcingSettings = useMemo(() => {
        if (orderType === 'renovations') {
            return selectedProperty?.sourcingSettingsReno;
        } else {
            return selectedProperty?.sourcingSettings;
        }
    }, [selectedProperty, orderType]);

    const sortingModeLabel: string = useMemo(() => {
        if (
            sortingMode === ProductPriceSortingModes.BestPrice ||
            !enablePreferredSuppliers
        ) {
            return t('Best price');
        } else if (sortingMode === ProductPriceSortingModes.Custom) {
            return t('Custom');
        }
        return t('Best price');
    }, [sortingMode, enablePreferredSuppliers, t]);

    const showNumbers = useMemo(() => {
        return (
            enablePreferredSuppliers &&
            sortingMode === ProductPriceSortingModes.Custom
        );
    }, [enablePreferredSuppliers, sortingMode]);

    const friendlyDescription = useMemo(() => {
        // Standard mode
        if (!enablePreferredSuppliers) {
            return t(
                'Quantities are distributed to suppliers with available stock, prioritized by best price.',
            );
        }

        // Preferred only mode
        if (preferredSuppliersOnly) {
            if (preferredSuppliers.length <= 1) {
                return t(
                    'Quantities are distributed to your preferred supplier only.',
                );
            }

            if (sortingMode === ProductPriceSortingModes.Custom) {
                return t(
                    'Quantities are distributed to preferred suppliers with available stock only, prioritized by custom order.',
                );
            }

            return t(
                'Quantities are distributed to preferred suppliers with available stock only, prioritized by best price.',
            );
        }

        // Highlight preferred mode
        if (preferredSuppliers.length <= 1) {
            return t(
                'Quantities are distributed to suppliers with available stock, prioritizing preferred suppliers first and then others, both by best price.',
            );
        }

        if (sortingMode === ProductPriceSortingModes.Custom) {
            return t(
                'Quantities are distributed to suppliers with available stock, prioritizing preferred suppliers first (by custom order) and then others by best price.',
            );
        }

        return t(
            'Quantities are distributed to suppliers with available stock, prioritizing preferred suppliers first and then others, both by best price.',
        );
    }, [
        enablePreferredSuppliers,
        preferredSuppliersOnly,
        preferredSuppliers.length,
        sortingMode,
        t,
    ]);

    /**
     * Memoized sorted available suppliers to prevent unnecessary re-sorting.
     * This is sorted once and reused throughout the component.
     */
    const sortedAvailableSuppliers = useMemo(() => {
        if (!listOfSuppliers) return [];
        return [...listOfSuppliers].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
    }, [listOfSuppliers]);

    /**
     * Memoized supplier filtering with optimized performance.
     *
     * This function processes suppliers into preferred and available lists:
     * - Preserves API order for preferred suppliers
     * - Uses Set/Map lookups for optimal performance
     * - Uses pre-sorted available suppliers for better performance
     */
    const {
        preferredSuppliers: memoizedPreferred,
        availableSuppliers: memoizedAvailable,
    } = useMemo(() => {
        if (!listOfSuppliers) {
            return { preferredSuppliers: [], availableSuppliers: [] };
        }

        const available: Supplier[] = [...sortedAvailableSuppliers];

        // Only do the preferred bit if there are actually preferred suppliers
        // We run even if disabled so you can toggle and not lose your preferred suppliers
        if (sourcingSettings?.preferredSuppliers?.length) {
            const preferred: Supplier[] = [];
            const preferredSupplierIds = new Set<string>();
            const availableSuppliersMap = new Map(
                available.map((supplier) => [supplier.id, supplier]),
            );

            sourcingSettings.preferredSuppliers.forEach(
                (preferredSupplier: Supplier) => {
                    preferredSupplierIds.add(preferredSupplier.id);
                    const foundSupplier = availableSuppliersMap.get(
                        preferredSupplier.id,
                    );
                    if (foundSupplier) {
                        preferred.push(foundSupplier);
                    }
                },
            );

            // Only alpha sort if sorting mode is best price
            if (sourcingSettings.mode === ProductPriceSortingModes.BestPrice) {
                preferred.sort((a, b) => a.name.localeCompare(b.name));
            }

            // Filter out preferred suppliers (already sorted)
            const filteredAvailable = available.filter(
                (supplier) => !preferredSupplierIds.has(supplier.id),
            );

            return {
                preferredSuppliers: preferred,
                availableSuppliers: filteredAvailable,
            };
        }

        // Return pre-sorted available suppliers
        return { preferredSuppliers: [], availableSuppliers: available };
    }, [
        sourcingSettings?.preferredSuppliers,
        sourcingSettings?.mode,
        sortedAvailableSuppliers,
        listOfSuppliers,
    ]);

    const resetConfigurationState = useCallback(() => {
        if (sourcingSettings && listOfSuppliers) {
            setEnablePreferredSuppliers(
                sourcingSettings.mode !==
                    ProductSourcingModes.AvailabilityPrice,
            );

            setPreferredSuppliersOnly(
                sourcingSettings.mode === ProductSourcingModes.OnlyPreferred,
            );

            setSortingMode(
                sourcingSettings.sortingMode ||
                    ProductPriceSortingModes.BestPrice,
            );
        }
    }, [sourcingSettings, listOfSuppliers]);

    useEffect(() => {
        if (hasPendingChanges) {
            dispatch(navigationBlockModalActions.setNavigationBlocked(true));
        } else {
            dispatch(navigationBlockModalActions.setNavigationBlocked(false));
        }

        return () => {
            dispatch(navigationBlockModalActions.setNavigationBlocked(false));
        };
    }, [hasPendingChanges, dispatch]);

    useEffect(() => {
        resetConfigurationState();
    }, [resetConfigurationState]);

    useEffect(() => {
        // Initialize state from memoized values only when data source changes
        if (listOfSuppliers) {
            setPreferredSuppliers(memoizedPreferred);
            setAvailableSuppliers(memoizedAvailable);
        }
    }, [memoizedPreferred, memoizedAvailable, listOfSuppliers]);

    const getProductSourcingMode = useCallback(
        (
            enablePreferredSuppliers: boolean,
            preferredSuppliersOnly: boolean,
        ) => {
            if (!enablePreferredSuppliers) {
                return ProductSourcingModes.AvailabilityPrice;
            }

            return preferredSuppliersOnly
                ? ProductSourcingModes.OnlyPreferred
                : ProductSourcingModes.HighlightPreferred;
        },
        [],
    );

    const hasNewSettings = useCallback(
        (
            config: {
                enablePreferredSuppliers?: boolean;
                preferredSuppliersOnly?: boolean;
                sortingMode?: string;
            } | null,
            preferredList: Supplier[] | null,
        ) => {
            setHasPendingChanges(true);
        },
        [],
    );

    /**
     * Applies sourcing settings to the property.
     *
     * This function handles both automatic and manual settings updates:
     * - Processes configuration changes from UI interactions
     * - Handles drag-and-drop reordering of preferred suppliers
     * - Builds complete settings object for API submission
     * - Maintains backward compatibility with existing state
     */
    const saveSettings = useCallback(async () => {
        setSaveInProgress(true);

        // Determine the new mode to save
        const newMode = getProductSourcingMode(
            enablePreferredSuppliers,
            preferredSuppliersOnly,
        );

        // Build out the settings to save based on the new state
        const settings: ProductSourcingSettings = {
            propertyId: selectedProperty?.id || '',
            mode: newMode,
            orderType: orderType,
            sortingMode: sortingMode,
            preferredSuppliersIds: preferredSuppliers?.map(
                (supplier) => supplier.id,
            ),
        };

        try {
            await onApplySettingsToProperty(settings);
            setHasPendingChanges(false);
            toast(t('Changes saved successfully'));
        } catch (error) {
            toast(t('Unknown Error'));
        } finally {
            setSaveInProgress(false);
        }
    }, [
        onApplySettingsToProperty,
        selectedProperty,
        preferredSuppliers,
        orderType,
        sortingMode,
        getProductSourcingMode,
        enablePreferredSuppliers,
        preferredSuppliersOnly,
        t,
    ]);

    /**
     * Cancels all pending changes and resets to the original state from the server.
     * This method resets all local state to match the original sourcingSettings
     * and clears the hasPendingChanges flag.
     */
    const cancelChanges = useCallback(() => {
        if (sourcingSettings && listOfSuppliers) {
            resetConfigurationState();
            setPreferredSuppliers(memoizedPreferred);
            setAvailableSuppliers(memoizedAvailable);
            setHasPendingChanges(false);
        }
    }, [
        sourcingSettings,
        listOfSuppliers,
        memoizedPreferred,
        memoizedAvailable,
        resetConfigurationState,
    ]);

    return {
        preferredSuppliers,
        setPreferredSuppliers,
        availableSuppliers,
        setAvailableSuppliers,
        sortedAvailableSuppliers,
        enablePreferredSuppliers,
        setEnablePreferredSuppliers,
        preferredSuppliersOnly,
        setPreferredSuppliersOnly,
        sortingMode,
        setSortingMode,
        isLoadingData,
        isSaveSettings,
        sortingModeLabel,
        showNumbers,
        saveInProgress,
        friendlyDescription,
        hasPendingChanges,
        saveSettings,
        hasNewSettings,
        cancelChanges,
    };
};
